import { useEffect, useState, useCallback } from 'react';
import {
  Button,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
} from '@nextui-org/react';
import { supabase } from 'common/src/supabase';
import { useLocation } from 'wouter';
import { Alert } from '../ui';

interface ConsistencyResult {
  exchange_rate_mismatches: number;
  total_mismatches: number;
  fob_mismatches: number;
}

interface DetailRow {
  settlement_id: number;
  reference_number: string;
  exporter_name: string;
  season: string;
  details: string;
}

type CheckType = 'exchange_rate' | 'totals' | 'fob';

const CHECK_LABELS: Record<CheckType, string> = {
  exchange_rate: 'Tipo de cambio',
  totals: 'Totales',
  fob: 'FOB',
};

const SettlementConsistencyBanner = () => {
  const [, setLocation] = useLocation();
  const [result, setResult] = useState<ConsistencyResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedCheck, setExpandedCheck] = useState<CheckType | null>(null);
  const [detailRows, setDetailRows] = useState<DetailRow[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const runCheck = useCallback(async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    setExpandedCheck(null);
    setDetailRows([]);
    setDetailError(null);
    const { data, error: rpcError } = await supabase.rpc(
      'check_settlement_consistency'
    );
    if (rpcError) {
      setError(rpcError.message);
    } else if (data && data.length > 0) {
      setResult(data[0]);
    } else if (data && !Array.isArray(data)) {
      setResult(data as ConsistencyResult);
    }
    setLoading(false);
  }, []);

  const loadDetails = useCallback(
    async (checkType: CheckType) => {
      if (expandedCheck === checkType) {
        setExpandedCheck(null);
        setDetailRows([]);
        setDetailError(null);
        return;
      }
      setDetailLoading(true);
      setExpandedCheck(checkType);
      setDetailError(null);
      const { data, error: rpcError } = await supabase.rpc(
        'get_settlement_consistency_details',
        {
          check_type: checkType,
        }
      );
      if (rpcError) {
        setDetailRows([]);
        setDetailError(rpcError.message);
      } else {
        setDetailRows((data as DetailRow[]) || []);
      }
      setDetailLoading(false);
    },
    [expandedCheck]
  );

  useEffect(() => {
    runCheck();
  }, [runCheck]);

  if (loading) return null;

  if (error) {
    return (
      <Alert variant='danger' showIcon className='mb-4'>
        <div className='flex flex-wrap items-center gap-3'>
          <span>Error al verificar consistencia de liquidaciones: {error}</span>
          <Button size='sm' variant='flat' onPress={runCheck}>
            Reintentar
          </Button>
        </div>
      </Alert>
    );
  }

  if (!result) return null;

  const { exchange_rate_mismatches, total_mismatches, fob_mismatches } = result;
  const hasIssues =
    exchange_rate_mismatches > 0 || total_mismatches > 0 || fob_mismatches > 0;

  if (!hasIssues) return null;

  const checks: { type: CheckType; count: number }[] = [
    { type: 'exchange_rate', count: exchange_rate_mismatches },
    { type: 'totals', count: total_mismatches },
    { type: 'fob', count: fob_mismatches },
  ].filter((c) => c.count > 0);
  const expandedCount =
    checks.find(({ type }) => type === expandedCheck)?.count || 0;

  return (
    <Alert variant='warning' showIcon className='mb-4'>
        <div className='flex flex-row items-center gap-3 flex-wrap'>
          <span className='font-semibold'>
            Inconsistencias detectadas en liquidaciones:
          </span>
          {checks.map(({ type, count }) => (
            <Button
              key={type}
              variant={expandedCheck === type ? 'solid' : 'flat'}
              size='sm'
              onPress={() => loadDetails(type)}
              isLoading={detailLoading && expandedCheck === type}
            >
              {expandedCheck === type ? 'Ocultar' : 'Ver'} {CHECK_LABELS[type]}:{' '}
              {count}
            </Button>
          ))}
          <Button
            size='sm'
            variant='flat'
            onPress={runCheck}
            isLoading={loading}
          >
            Actualizar
          </Button>
        </div>

        {expandedCheck && (
          <div className='mt-3 font-normal text-foreground'>
            <p className='mb-2'>
              Detalle — {CHECK_LABELS[expandedCheck]} · {expandedCount}{' '}
              hallazgos
            </p>
            {detailLoading ? (
              <p className='text-sm text-default-500'>Cargando...</p>
            ) : detailError ? (
              <Alert variant='danger'>
                No se pudo cargar el detalle: {detailError}
              </Alert>
            ) : detailRows.length === 0 ? (
              <p className='text-sm text-default-500'>
                No hay liquidaciones que coincidan con este control. Actualiza
                el resumen para recalcular los conteos.
              </p>
            ) : (
              <div>
                <p className='mb-2 text-xs text-default-500'>
                  {detailRows.length} liquidaciones afectadas
                </p>
                <div className='max-h-[360px] overflow-y-auto'>
                  <Table
                    aria-label='Detalle de inconsistencias'
                    removeWrapper
                    isCompact
                  >
                    <TableHeader>
                      <TableColumn>ID</TableColumn>
                      <TableColumn>Referencia</TableColumn>
                      <TableColumn>Exportadora</TableColumn>
                      <TableColumn>Temporada</TableColumn>
                      <TableColumn>Detalle</TableColumn>
                      <TableColumn>Acción</TableColumn>
                    </TableHeader>
                    <TableBody>
                      {detailRows.map((row) => (
                        <TableRow key={row.settlement_id}>
                          <TableCell>{row.settlement_id}</TableCell>
                          <TableCell>{row.reference_number}</TableCell>
                          <TableCell>{row.exporter_name}</TableCell>
                          <TableCell>{row.season}</TableCell>
                          <TableCell className='text-xs max-w-md whitespace-pre-wrap'>
                            {row.details}
                          </TableCell>
                          <TableCell>
                            <Button
                              size='sm'
                              variant='flat'
                              onPress={() =>
                                setLocation(
                                  `/settlements/details/${row.settlement_id}`
                                )
                              }
                            >
                              Abrir liquidación
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </div>
        )}
    </Alert>
  );
};

export default SettlementConsistencyBanner;
